/**
 * Home real de Staff (006 · T026 · US1; research R-4, R-6, R-16, R-19, R-20, R-30, R-31;
 * contracts/staff-ui.md §3).
 *
 * Compone tres lecturas del servidor —nunca calcula ninguna regla de asistencia (FR-003)—:
 *
 * 1. `me` (ya resuelto por `useAccessGate`, no se vuelve a pedir aquí): nombre y sedes.
 * 2. `GET /attendance/state` (`useAttendanceState`, `staleTime: 0`): estado, acción, aviso,
 *    `server_time`.
 * 3. `GET /attendance/marks?limit=3` (`useRecentDays`): últimas marcaciones y las marcas de hoy
 *    que alimentan la tarjeta "Tu jornada".
 *
 * El saludo se pinta **sin franja** ("Hola, María 👋") mientras `attendance/state` no llegó
 * (R-6): `me` ya está disponible en cuanto se monta este componente, así que el nombre nunca
 * espera a la red. Ambas lecturas salen en paralelo (R-31): ninguna espera a la otra.
 *
 * T037 (US3, research R-30): `InstallCard` se monta después de "Últimas marcaciones", **antes**
 * del botón del pie — nunca más de tres cards en el viewport inicial (tarjeta de jornada, lista de
 * últimas marcaciones y, solo en Android sin instalar, la card de instalación). Ella misma decide
 * si pintarse (Android, no standalone, no pospuesta): aquí no se repite esa condición.
 *
 * 007 · T034/T068 (contracts/ui.md §1.2, research R-15): `RegularizationCard` va **encima** de
 * "Tu jornada" cuando hay algo accionable (`GET /attendance/regularizations`, lectura paralela a
 * las otras dos; si falla, la home sigue igual sin la card). Si la entrada de la jornada excedida
 * (`last_mark` del estado) es el ancla de un ítem regularizable, la card **reemplaza** el aviso
 * `home-open-shift`; si no (fuera del plazo, sin propuestas), el aviso se mantiene. Con la card en
 * pantalla, la de instalación cede su lugar: nunca más de tres cards en el viewport (§8.1, R-30
 * de la 006).
 *
 * 008 · T096 (contracts/ui.md §2.1, research R-25): la línea "Tu turno hoy" va **dentro** de "Tu
 * jornada" (`TodayShiftLine`, ranura `schedule`): no suma cards. Su lectura (`GET /attendance/days`) es
 * paralela a las demás y, si falla o la empresa no usa horarios, no pinta nada.
 */
import { Link } from '@tanstack/react-router';

import type {
  AttendanceStateResponse,
  StaffMe,
  StaffRegularizationsResponse,
} from '@fermin/contracts';

import { useOnlineStatus } from '../../api/online';
import { Button, buttonClasses } from '../../components/ui/button';
import { GenericErrorScreen } from '../../components/ui/generic-error';
import { OfflineBanner, OfflineState } from '../../components/ui/offline-state';
import { Screen } from '../../components/ui/screen';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import { TabBar } from '../../components/ui/tab-bar';
import { formatWeekdayDate, greetingKey, t } from '../../i18n';
import { useAttendanceState } from '../attendance/api';
import { useRecentDays } from '../history/api';
import { InstallCard } from '../install/install-card';
import { useStaffRegularizations } from '../regularization/api';
import { primaryRegularization, RegularizationCard } from '../regularization/regularization-card';
import { useStaffDays } from '../schedule/api';
import { TodayShiftLine } from '../schedule/today-shift';

import { journeyCardOf } from './journey';
import { JourneyCard } from './journey-card';
import { RecentMarks } from './recent-marks';

import type { ApiError } from '../../api/client';
import type { UseQueryResult } from '@tanstack/react-query';

/** Zona horaria de la sede principal (`is_primary`); sin sede, la de la empresa (R-6). */
function primaryTimezone(me: StaffMe): string {
  return me.sites.find((site) => site.is_primary)?.timezone ?? me.company.default_timezone;
}

/** Primer nombre de `first_names` ("María José" → "María"). */
function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}

/**
 * Aviso de jornada sin salida (FR-004), conservado de T054/US5. Vive junto a la tarjeta y no la
 * reemplaza: es informativo y nunca bloquea nada.
 */
function OpenShiftNotice() {
  return (
    <p
      data-testid="home-open-shift"
      role="status"
      className="rounded-fm-md bg-fm-warning-soft p-fm-3 text-fm-base text-fm-warning-text"
    >
      {t('access.home.openShift')}
    </p>
  );
}

interface HomeMarkActionProps {
  attendance: UseQueryResult<AttendanceStateResponse, ApiError>;
  online: boolean;
}

/**
 * Botón de marcación del pie de Inicio (T098, ampliado en 006 · T026/T027): el único elemento
 * coral sólido de la pantalla (§8.1). La revalidación de la acción antes de la cámara la hace
 * `/marcar` (T027, FR-005); aquí solo se envía la acción **mostrada** en `?accion=`.
 */
function HomeMarkAction({ attendance, online }: HomeMarkActionProps) {
  if (!online) {
    return (
      <Button data-testid="home-mark-offline" disabled>
        {t('shell.offline.markButton')}
      </Button>
    );
  }

  if (attendance.isPending) {
    return (
      <Button
        data-testid="home-mark-loading"
        loading
        loadingText={t('common.states.loading')}
        disabled
      />
    );
  }

  if (attendance.isError) {
    return (
      <div className="flex flex-col gap-fm-2" data-testid="home-mark-error">
        <p role="alert" className="text-fm-sm text-fm-danger-text">
          {t('attendance.error.title')}
        </p>
        <Button onClick={() => void attendance.refetch()}>{t('common.buttons.retry')}</Button>
      </div>
    );
  }

  if (attendance.data.state === 'UNRESOLVED' || attendance.data.proposed_action === null) {
    return (
      <p
        data-testid="home-mark-unresolved"
        role="status"
        className="text-fm-sm text-fm-text-secondary"
      >
        {t('common.errors.ATTENDANCE_STATE_UNRESOLVED')}
      </p>
    );
  }

  const action = attendance.data.proposed_action;
  const label =
    action === 'CHECK_IN' ? t('attendance.actions.checkIn') : t('attendance.actions.checkOut');

  return (
    <Link
      to="/marcar"
      search={{ accion: action }}
      data-testid="home-mark-button"
      className={buttonClasses()}
    >
      {label}
    </Link>
  );
}

function HomeLoading() {
  return (
    <div data-testid="home-loading" className="flex flex-col gap-fm-4">
      <SkeletonText label={t('common.states.loading')}>
        <Skeleton radius="lg" className="h-32 w-full" />
        <Skeleton radius="lg" className="h-40 w-full" />
      </SkeletonText>
    </div>
  );
}

/**
 * ¿La entrada abierta de la jornada excedida (`last_mark`) es el ancla de un ítem regularizable?
 * Se compara por tipo, instante y sede (el estado no trae el id de la marca). Sin datos de
 * regularización, `false`: el aviso de la 006 se mantiene.
 */
function openShiftIsRegularizable(
  state: AttendanceStateResponse | undefined,
  regularizations: StaffRegularizationsResponse | undefined,
): boolean {
  const last = state?.last_mark;
  if (!last || regularizations === undefined) {
    return false;
  }
  return regularizations.items.some(
    (item) =>
      item.can_request &&
      item.anchor.type === last.type &&
      item.anchor.site.id === last.site.id &&
      new Date(item.anchor.server_timestamp).getTime() ===
        new Date(last.server_timestamp).getTime(),
  );
}

export interface HomeScreenProps {
  me: StaffMe;
}

export function HomeScreen({ me }: HomeScreenProps) {
  const online = useOnlineStatus();
  const attendance = useAttendanceState();
  const recent = useRecentDays();
  const regularizations = useStaffRegularizations();
  const staffDays = useStaffDays();
  const timezone = primaryTimezone(me);
  const name = firstName(me.worker.first_names);
  const nav = <TabBar active="home" />;

  const retryAll = () => {
    void attendance.refetch();
    void recent.refetch();
  };

  /**
   * El historial (`recent`) es el que decide si hay algo que pintar: alimenta "Últimas
   * marcaciones" **y**, junto con `attendance`, la tarjeta "Tu jornada". Si `attendance` falla
   * solo (T054/T098, conservado tal cual): el saludo se queda en su variante sin franja, la
   * tarjeta simplemente no aparece y el botón muestra su propio `home-mark-error` — el resto de
   * la pantalla sigue en pie. Solo cuando `recent` no tiene nada que mostrar se reemplaza toda la
   * pantalla por `OfflineState`/`GenericErrorScreen` (R-20, R-24).
   */
  if (recent.data === undefined) {
    if (!online || recent.error?.status === 0) {
      return (
        <Screen data-testid="home-placeholder" nav={nav}>
          <OfflineState onRetry={retryAll} />
        </Screen>
      );
    }
    if (recent.isError) {
      return <GenericErrorScreen nav={nav} onRetry={retryAll} />;
    }
  }

  const greeting = attendance.data
    ? t(`home.greeting.${greetingKey(attendance.data.server_time, timezone)}`, { name })
    : t('home.greeting.loading', { name });
  const date = attendance.data ? formatWeekdayDate(attendance.data.server_time, timezone) : null;

  const card =
    attendance.data && recent.data
      ? journeyCardOf({ state: attendance.data, today: recent.data.today, days: recent.data.days })
      : null;

  const regularizationData =
    regularizations.data !== undefined && primaryRegularization(regularizations.data) !== null
      ? regularizations.data
      : null;
  const hasRegularizationCard = regularizationData !== null;

  return (
    <Screen
      data-testid="home-placeholder"
      nav={nav}
      footer={<HomeMarkAction attendance={attendance} online={online} />}
    >
      <h1
        data-testid="home-greeting"
        className="font-display text-fm-2xl font-extrabold text-fm-text"
      >
        {greeting}
      </h1>
      {date !== null ? (
        <p data-testid="home-date" className="text-fm-sm text-fm-text-secondary">
          {date}
        </p>
      ) : null}

      {!online && recent.data !== undefined ? <OfflineBanner /> : null}

      {recent.data === undefined ? (
        <HomeLoading />
      ) : (
        <>
          {card?.kind === 'OPEN_SHIFT_EXCEEDED' &&
          !openShiftIsRegularizable(attendance.data, regularizations.data) ? (
            <OpenShiftNotice />
          ) : null}
          {regularizationData !== null ? (
            <RegularizationCard data={regularizationData} online={online} />
          ) : null}
          {card !== null ? (
            <JourneyCard
              card={card}
              schedule={
                staffDays.data !== undefined ? <TodayShiftLine data={staffDays.data} /> : null
              }
            />
          ) : null}
          <RecentMarks days={recent.data.days} today={recent.data.today} />
          {hasRegularizationCard ? null : <InstallCard />}
        </>
      )}
    </Screen>
  );
}
